import { loadQueue, saveQueue } from './offline-queue.storage';
import { getTaskHandler } from './task-registry';
import {
  OfflineCurrentCustomerMetadata,
  PersistedTask,
  OfflineTaskType,
  OfflineExecutionContext,
  OfflineTaskPayloadMap,
} from '@zonofi/zono-money-store';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

class OfflineQueue {
  private queue: PersistedTask[] = [];
  private context: OfflineExecutionContext = {};
  private running = false;
  private contextByCustomer: Record<string, OfflineExecutionContext> = {};

  async hydrate() {
    if (this.running) return;
    const persisted = await loadQueue();
    this.queue = persisted.queue;
    this.context = persisted.context;
    this.contextByCustomer = persisted.contextByCustomer;
  }

  async enqueueOrUpdate<T extends OfflineTaskType>(
    type: T,
    entityKey: string,
    updater: (
      prev: OfflineTaskPayloadMap[T] | undefined
    ) => OfflineTaskPayloadMap[T],
    customer?: OfflineCurrentCustomerMetadata
  ) {
    const task = this.queue.find(
      (t) => t.type === type && t.entityKey === entityKey
    );

    if (task) {
      task.payload = updater(task.payload as OfflineTaskPayloadMap[T]);
      task.retries = 0;
    } else {
      this.queue.push({
        id: Date.now().toString(),
        type,
        entityKey,
        customerMetadata: customer,
        payload: updater(undefined),
        retries: 0,
        timestamp: Date.now(),
      });
    }

    await this.persist();
  }

  async updateTaskById<T extends OfflineTaskType>(
    id: string,
    payload: OfflineTaskPayloadMap[T]
  ) {
    const task = this.queue.find((t): t is PersistedTask<T> => t.id === id);
    if (!task) return;

    task.payload = payload;
    task.retries = 0;
    await this.persist();
  }

  async removeTaskById(id: string) {
    if (this.running && this.queue[0]?.id === id) {
      throw new Error('Cannot remove task currently being processed');
    }

    const index = this.queue.findIndex((t) => t.id === id);
    if (index === -1) return false;

    this.queue.splice(index, 1);
    await this.persist();
    return true;
  }

  async sync() {
    if (this.running) return;
    this.running = true;

    while (this.queue.length > 0) {
      const task = this.queue[0];

      try {
        const handler = getTaskHandler<typeof task.type>(task.type);

        const meta = {
          id: task.id,
          timestamp: task.timestamp,
          ...(task.customerMetadata ?? {}),
        };

        await handler(task.payload as any, meta, this.context);

        this.queue.shift();
        await this.persist();
        const delay = Math.min(1000 * 2 ** task.retries, 5000);
        await sleep(delay);
      } catch (e: any) {
        console.error('[OfflineQueue.sync] Task failed, stopping sync', {
          task,
          e,
        });
        task.retries += 1;
        await this.persist();
        this.running = false;
        let errorMessage =
          typeof e === 'string'
            ? e
            : typeof e.message === 'string'
              ? e.message
              : typeof e.data.message === 'string'
                ? e.data.message
                : 'Something went wrong during sync!!!';
        const error = `Task failed, stopping sync\ntask:${task.type}\nerror:${errorMessage}`;
        throw new Error(error);
      }
    }

    this.running = false;
  }

  async syncInParallel() {
    if (this.running) return;
    this.running = true;

    try {
      // 1. Group tasks by customer
      const grouped = this.queue.reduce<Record<string, PersistedTask[]>>(
        (acc, task) => {
          const customerId = task.customerMetadata?.customerId;
          if (!customerId) return acc;

          if (!acc[customerId]) acc[customerId] = [];
          acc[customerId].push(task);
          return acc;
        },
        {}
      );

      // 2. Create one pipeline per customer
      const customerPipelines = Object.entries(grouped).map(
        ([customerId, tasks]) => this.runCustomerPipeline(customerId, tasks)
      );

      // 3. Run all customers in parallel
      await Promise.all(customerPipelines);
    } finally {
      this.running = false;
    }
  }

  getSnapshot() {
    return [...this.queue];
  }

  getSize() {
    return this.queue.length;
  }

  private async runCustomerPipeline(
    customerId: string,
    tasks: PersistedTask[]
  ) {
    for (const task of tasks) {
      try {
        const handler = getTaskHandler<typeof task.type>(task.type);

        const meta = {
          id: task.id,
          timestamp: task.timestamp,
          ...(task.customerMetadata ?? {}),
        };

        const ctx =
          this.contextByCustomer[customerId] ??
          (this.contextByCustomer[customerId] = {});

        await handler(task.payload as any, meta, ctx);

        // Remove task from global queue
        this.queue = this.queue.filter((t) => t.id !== task.id);

        await this.persist();

        const delay = Math.min(500 * 2 ** task.retries, 4000);
        await sleep(delay);
      } catch (e) {
        task.retries += 1;
        await this.persist();
        throw e;
      }
    }
  }

  private async persist() {
    await saveQueue({
      queue: this.queue,
      context: this.context,
      contextByCustomer: this.contextByCustomer,
    });
  }

  async clearQueue() {
    this.queue = [];
    this.context = {};
    this.contextByCustomer = {};

    await this.persist();
    return true;
  }
}

export const offlineQueue = new OfflineQueue();
